import {
  Box,
  Dialog,
  DialogContent,
  Link,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import {
  ImageContainer,
  LoginContainer,
  LoginStyle,
  PageContainer,
  StyledContainer,
} from '../../commonStyle/loginStyle';
import Logo from '../../image/logo.png';
import wardImage1 from '../../image/wardImage1.jpg';
import {
  authResetState,
  updatePasswordRequest,
} from '../../redux/auth/authActions';

function PasswordResetPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { success, successMessage, error, errorMessage } = useSelector(
    (state) => state.auth?.forgotPassword || {}
  );
  const passwordState = useSelector(
    (state) => state.auth?.forgotPassword || {}
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });
  const newPassword = watch('newPassword');
  const onSubmit = (data) => {
    const email = localStorage.getItem('email');
    const { confirmPassword } = data;
    dispatch(updatePasswordRequest({ email, confirmPassword }));
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    if (error && errorMessage) {
      setOpenEditDialog(true);
    }
    dispatch(authResetState());
  }, [error, errorMessage, setError, dispatch]);

  useEffect(() => {
    if (success) {
      setOpenSuccessDialog(true);
      const timer = setTimeout(() => {
        navigate('/');
        dispatch(authResetState());
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [success, successMessage, dispatch, navigate]);

  return (
    <PageContainer>
      <StyledContainer>
        <ImageContainer>
          <Box sx={LoginStyle.boxOn}>
            <Box sx={LoginStyle.boxTwo}>
              <Box
                component="img"
                src={Logo}
                alt="XRay Logo"
                sx={LoginStyle.boxThree}
              />
              <Typography
                variant="h5"
                component="h1"
                sx={LoginStyle.typographyBold}
              >
                Government Hospital
              </Typography>
            </Box>
            {!isMobile && (
              <Box sx={LoginStyle.boxFore}>
                <Box
                  component="img"
                  src={wardImage1}
                  alt="Hospital facility"
                  sx={LoginStyle.boxFive}
                />
                <Typography variant="h3" sx={LoginStyle.typographyOne}>
                  " Pay Ward "
                </Typography>
              </Box>
            )}
          </Box>
        </ImageContainer>
        <LoginContainer elevation={0}>
          <Box sx={LoginStyle.boxSix}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              component="h1"
              sx={LoginStyle.typographyTwo}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={LoginStyle.textSecondary}
            >
              Enter your new password and confirm it to reset your password.
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={LoginStyle.formField}>
                <Typography variant="body2" sx={LoginStyle.typographyThree}>
                  New Password
                </Typography>
                <TextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="New Password"
                  variant="outlined"
                  {...register('newPassword', {
                    required: 'Password is required',
                    maxLength: 8,
                    showCharCount: false,
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      message:
                        'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character',
                    },
                  })}
                  InputProps={{
                    sx: LoginStyle.inputField,
                    endAdornment: (
                      <Box
                        sx={{ cursor: 'pointer', display: 'flex' }}
                        onClick={toggleNewPasswordVisibility}
                      >
                        {showNewPassword ? (
                          <EyeOffIcon size={20} color="#666" />
                        ) : (
                          <EyeIcon size={20} color="#666" />
                        )}
                      </Box>
                    ),
                  }}
                  size="small"
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                />
              </Box>
              <Box sx={LoginStyle.formField}>
                <Typography variant="body2" sx={LoginStyle.typographyThree}>
                  Confirm Password
                </Typography>
                <TextField
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm Password"
                  variant="outlined"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === newPassword || 'Passwords do not match',
                  })}
                  InputProps={{
                    sx: LoginStyle.inputField,
                    endAdornment: (
                      <Box
                        sx={{ cursor: 'pointer', display: 'flex' }}
                        onClick={toggleConfirmPasswordVisibility}
                      >
                        {showConfirmPassword ? (
                          <EyeOffIcon size={20} color="#666" />
                        ) : (
                          <EyeIcon size={20} color="#666" />
                        )}
                      </Box>
                    ),
                  }}
                  size="small"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                />
              </Box>
              <Box sx={LoginStyle.rememberMeContainer}>
                <CustomButton
                  type="submit"
                  text="Submit"
                  variant="contained"
                  disableElevation
                  size="small"
                  disabled={passwordState?.inProgress}
                  loading={passwordState?.inProgress}
                />
              </Box>
            </form>
            <Box sx={LoginStyle.footerContainer}>
              <Typography variant="body2" color="textSecondary">
                Remembered your password?{' '}
                <Link href="/" underline="none" sx={LoginStyle.signUpLink}>
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        </LoginContainer>
      </StyledContainer>
      <Dialog
        open={openSuccessDialog}
        aria-labelledby="login-success-dialog"
        PaperProps={{
          sx: {
            borderRadius: '8px',
            maxWidth: '500px',
            padding: '16px',
            textAlign: 'center',
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 2, width: '400px' }}>
          <CheckCircleIcon
            sx={{
              color: '#4CAF50',
              fontSize: 64,
              mb: 2,
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
            Login Successful!
          </Typography>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openSuccessDialog}
        aria-labelledby="login-success-dialog"
        PaperProps={{
          sx: {
            borderRadius: '8px',
            maxWidth: '500px',
            padding: '16px',
            textAlign: 'center',
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 2, width: '400px' }}>
          <CheckCircleIcon
            sx={{
              color: '#4CAF50',
              fontSize: 64,
              mb: 2,
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
            Update Password Successfully!
          </Typography>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default PasswordResetPage;
